import {Box, CircularProgress, Typography} from '@mui/material';

export interface LoadingStateProps {
    /** Optional message to display below spinner */
    message?: string;
    /** Secondary message (smaller text) */
    submessage?: string;
    /** Spinner size in pixels (default: 40) */
    size?: number;
    /** Layout variant */
    variant?: 'centered' | 'inline' | 'fullHeight';
    /** Custom padding (default: 2) */
    padding?: number;
}

/**
 * Unified loading state component with spinner and optional message.
 *
 * Variants:
 * - centered: Centered horizontally with padding (default)
 * - inline: Inline display for use in buttons or small areas
 * - fullHeight: Centered both horizontally and vertically, fills available height
 */
export function LoadingState({
    message,
    submessage,
    size = 40,
    variant = 'centered',
    padding = 2,
}: LoadingStateProps) {
    if (variant === 'inline') {
        return <CircularProgress size={size} />;
    }

    if (variant === 'fullHeight') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '50vh',
                    gap: 2,
                }}
            >
                <CircularProgress size={size} />
                {message && <Typography variant="h6">{message}</Typography>}
                {submessage && (
                    <Typography variant="body2" sx={{
                        color: "text.secondary"
                    }}>
                        {submessage}
                    </Typography>
                )}
            </Box>
        );
    }

    // Default: centered
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: message ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                p: padding,
                gap: message ? 1 : 0,
            }}
        >
            <CircularProgress size={size} />
            {message && (
                <Typography variant="body2" sx={{
                    color: "text.secondary"
                }}>
                    {message}
                </Typography>
            )}
        </Box>
    );
}

export default LoadingState;
